import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: 'Is this for beginners or advanced people?',
    answer: 'Both. The foundation tracks (Thinking-Skills, AI Rock Star, Mindful Machine, AI Literacy) assume no prior AI expertise. The Domain Labs are for when you\'re ready to apply those skills in markets, manufacturing, science, entrepreneurship, or civic projects.'
  },
  {
    question: 'Do I need to know how to code?',
    answer: 'No. Many lessons involve writing, reasoning, and designing workflows. Some tracks (like Small Products with Workers or certain labs) do involve light coding, but we\'ll clearly mark those and provide ramp-up support.'
  },
  {
    question: 'How much time do I need?',
    answer: 'If you can find 1-2 hours per week, you can make real progress. Each lesson is designed to be bite-sized (30-45 minutes) and reflective, not crammed.'
  },
  {
    question: 'What tools do I need?',
    answer: 'At minimum: a web browser and access to at least one major AI assistant (ChatGPT, Claude, Grok, etc.). We\'ll show you how to work with whichever tools you already have.'
  },
  {
    question: 'Will there be live sessions or is it all self-paced?',
    answer: 'The core Academy is mostly self-paced, with the AI-Trainer to guide you. Over time, we\'ll add optional cohorts and live seminars for certain tracks and labs.'
  },
  {
    question: 'Is this just for tech people?',
    answer: 'No. The point of Northcoast AI Academy is to help non-"tech bros" think clearly and use AI well: teachers, therapists, social workers, ops and manufacturing folks, writers, designers, community builders, and people who feel a bit overwhelmed by it all.'
  },
  {
    question: 'Is this a replacement for college or a coding bootcamp?',
    answer: 'No. Think of it as an AI-era companion to whatever you already do. If you\'re in school, this helps you learn and use AI ethically and effectively. If you\'re working, this helps you automate tasks and make better decisions. If you\'re between things, this helps you orient and skill up for what\'s coming next.'
  },
  {
    question: 'I run a small business \u2014 is this for me?',
    answer: 'Especially you. Google and the Chamber of Commerce will teach you to use AI for free, and you should let them. We teach the part they leave out: how to tell when AI is wrong before it costs you a customer \u2014 a made-up fact in a customer email, an invented policy from your website chatbot, a too-good invoice that is actually a scam. If your name is on the business, verification is the AI skill that protects it.'
  },
  {
    question: 'How does pricing work?',
    answer: 'We\'re in early access, so pricing is still being finalized. The current plan: a generous free tier to explore the basics, a mid-tier for full access to core tracks and some labs, and a higher tier for all labs, seminars, and heavier AI-Trainer use. We\'ll be transparent and keep it fair.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-12" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>

      <div className="max-w-3xl mx-auto space-y-4">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          const headingId = `faq-heading-${index}`;
          const panelId = `faq-panel-${index}`;

          return (
            <div
              key={index}
              className="border border-gray-700 rounded-lg overflow-hidden"
            >
              <h3>
                <button
                  id={headingId}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-800/50 hover:bg-gray-800 transition-colors"
                >
                  <span className="font-medium text-white">{item.question}</span>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
              </h3>

              <div
                id={panelId}
                role="region"
                aria-labelledby={headingId}
                hidden={!isOpen}
                className={isOpen ? 'p-4 bg-gray-900/50 border-t border-gray-700' : ''}
              >
                {isOpen && <p className="text-gray-400">{item.answer}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
